import * as Sentry from '@sentry/nextjs';
import type { MonitoringPort, MonitoringLevel, MonitoringUser } from '@/core/ports/external';

const levelMap: Record<MonitoringLevel, Sentry.SeverityLevel> = {
  debug: 'debug',
  info: 'info',
  warning: 'warning',
  error: 'error',
  fatal: 'fatal',
};

function withExtras(context: Record<string, unknown> | undefined, fn: () => void) {
  if (!context || Object.keys(context).length === 0) {
    fn();
    return;
  }

  Sentry.withScope((scope) => {
    scope.setExtras(context);
    fn();
  });
}

export const sentryMonitoring: MonitoringPort = {
  captureException(error, context) {
    withExtras(context, () => {
      Sentry.captureException(error);
    });
  },
  captureMessage(message, level = 'info', context) {
    withExtras(context, () => {
      Sentry.captureMessage(message, levelMap[level]);
    });
  },
  log(level, message, context) {
    withExtras(context, () => {
      Sentry.captureMessage(message, levelMap[level]);
    });
  },
  setUser(user: MonitoringUser | null) {
    Sentry.setUser(user ?? null);
  },
  clearUser() {
    Sentry.setUser(null);
  },
  setTag(key, value) {
    Sentry.setTag(key, value);
  },
  setContext(name, context) {
    Sentry.setContext(name, context);
  },
  async flush(timeoutMs = 2000) {
    return Sentry.flush(timeoutMs);
  },
};
